import { supabase } from '../lib/supabase';


export const referralService = {
  // Get or create user's referral link
  async getReferralLink(userId) {
    try {
      // Check if user already has a referral link
      const { data: existing, error: fetchError } = await supabase
        ?.from('referral_links')?.select('*')?.eq('user_id', userId)
        ?.single();

      if (existing) {
        return {
          id: existing?.id,
          referralCode: existing?.referral_code,
          totalSignups: existing?.total_signups || 0,
          successfulReferrals: existing?.successful_referrals || 0,
          totalPointsEarned: existing?.total_points_earned || 0,
          isActive: existing?.is_active,
          createdAt: existing?.created_at
        };
      }

      // Create new referral link if doesn't exist
      const { data: newLink, error: createError } = await supabase
        ?.rpc('generate_referral_code')
        ?.single();

      if (createError) throw createError;

      const { data: created, error: insertError } = await supabase
        ?.from('referral_links')
        ?.insert({
          user_id: userId,
          referral_code: newLink
        })
        ?.select()
        ?.single();

      if (insertError) throw insertError;

      return {
        id: created?.id,
        referralCode: created?.referral_code,
        totalSignups: 0,
        successfulReferrals: 0,
        totalPointsEarned: 0,
        isActive: true,
        createdAt: created?.created_at
      };
    } catch (error) {
      console.error('Error getting referral link:', error);
      throw error;
    }
  },

  // Get referral signups for a user
  async getReferralSignups(userId) {
    try {
      const { data, error } = await supabase
        ?.from('referral_signups')
        ?.select('*')
        ?.eq('referrer_user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(signup => ({
        id: signup?.id,
        referredEmail: signup?.referred_email,
        status: signup?.status,
        signupBonusAwarded: signup?.signup_bonus_awarded,
        firstTransactionBonusAwarded: signup?.first_transaction_bonus_awarded,
        retentionBonusAwarded: signup?.retention_bonus_awarded,
        totalBonusPoints: signup?.total_bonus_points || 0,
        signupDate: signup?.signup_date,
        activationDate: signup?.activation_date,
        retentionDate: signup?.retention_date,
        createdAt: signup?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching referral signups:', error);
      throw error;
    }
  },

  // Get referral statistics
  async getReferralStats(userId) {
    try {
      const [linkData, signupsData] = await Promise.all([
        this.getReferralLink(userId),
        this.getReferralSignups(userId)
      ]);

      const pendingSignups = signupsData?.filter(s => s?.status === 'pending')?.length || 0;
      const activeSignups = signupsData?.filter(s => s?.status === 'activated')?.length || 0;
      const completedSignups = signupsData?.filter(s => s?.status === 'completed')?.length || 0;

      return {
        totalFriendsReferred: linkData?.totalSignups || 0,
        pendingSignups,
        activeSignups,
        successfulConversions: completedSignups,
        totalBonusPoints: linkData?.totalPointsEarned || 0,
        conversionRate: linkData?.totalSignups > 0 
          ? ((completedSignups / linkData?.totalSignups) * 100)?.toFixed(1)
          : 0
      };
    } catch (error) {
      console.error('Error fetching referral stats:', error);
      throw error;
    }
  },

  // Generate shareable referral URL
  generateReferralUrl(referralCode) {
    const baseUrl = window.location?.origin;
    return `${baseUrl}/register?ref=${referralCode}`;
  },

  // Copy referral link to clipboard
  async copyReferralLink(referralCode) {
    const url = this.generateReferralUrl(referralCode);
    try {
      await navigator.clipboard?.writeText(url);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  },

  // Track referral signup (called during registration)
  async trackReferralSignup(referralCode, referredEmail) {
    try {
      // Get referral link by code
      const { data: referralLink, error: linkError } = await supabase
        ?.from('referral_links')
        ?.select('id, user_id')
        ?.eq('referral_code', referralCode)
        ?.eq('is_active', true)
        ?.single();

      if (linkError || !referralLink) {
        throw new Error('Invalid referral code');
      }

      // Create referral signup record
      const { data, error } = await supabase
        ?.from('referral_signups')
        ?.insert({
          referral_link_id: referralLink?.id,
          referrer_user_id: referralLink?.user_id,
          referred_email: referredEmail,
          status: 'pending'
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        referrerId: referralLink?.user_id
      };
    } catch (error) {
      console.error('Error tracking referral signup:', error);
      throw error;
    }
  },

  // Update referral signup status (called by system events)
  async updateReferralStatus(referredUserId, newStatus) {
    try {
      const { data, error } = await supabase
        ?.from('referral_signups')
        ?.update({ 
          status: newStatus,
          referred_user_id: referredUserId
        })
        ?.eq('referred_user_id', referredUserId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating referral status:', error);
      throw error;
    }
  },

  // Get referral bonus structure
  getReferralBonusStructure() {
    return [
      {
        id: 'signup',
        title: 'Friend Signs Up',
        points: 50,
        description: 'Earn points when your friend creates an account',
        icon: 'UserPlus',
        color: 'var(--color-primary)'
      },
      {
        id: 'activation',
        title: 'Friend Activates Account',
        points: 100,
        description: 'Bonus when your friend completes their first transaction',
        icon: 'CheckCircle',
        color: 'var(--color-success)'
      },
      {
        id: 'retention',
        title: '30-Day Retention',
        points: 200,
        description: 'Extra bonus when your friend stays active for 30 days',
        icon: 'Trophy',
        color: 'var(--color-warning)'
      }
    ];
  },

  // Get milestone bonuses
  getMilestoneBonuses() {
    return [
      {
        id: 'milestone_5',
        title: '5 Successful Referrals',
        bonusPoints: 500,
        description: 'Refer 5 friends who complete 30-day retention',
        icon: 'Award'
      },
      {
        id: 'milestone_10',
        title: '10 Successful Referrals',
        bonusPoints: 1500,
        description: 'Refer 10 friends who complete 30-day retention',
        icon: 'Star'
      },
      {
        id: 'milestone_25',
        title: '25 Successful Referrals',
        bonusPoints: 5000,
        description: 'Refer 25 friends who complete 30-day retention',
        icon: 'Crown'
      }
    ];
  }
};